import { append } from '@akin-home/arrays';
import { ChecklistModel, createChecklistItem } from '@akin-home/checklists';
import { LargeInput } from '@akin-home/forms/native';
import { Text } from '@akin-home/text/native';
import { colors, font_variant_styles } from '@akin-home/theme';
import { UserModel } from '@akin-home/user';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useRef } from 'react';
import { TextInput, useWindowDimensions, View } from 'react-native';
import Animated from 'react-native-reanimated';
import styled from 'styled-components/native';
import { ChecklistFieldItem } from '../../checklists/components/ChecklistFieldItem';
import { EmojiInputField } from '../../forms/components/EmojiInputField';
import { dimensions, layout, palette } from '../../theme';
import { spacing } from '../../theme/spacing';
import { filterExcludeByID } from '../../utils/records';
import NewButton from '../Button/NewButton';
import Checkbox from '../Checkbox';
import { DropdownFamily } from '../DropdownFamily/DropdownFamily';
import { FloatingActions, useFloatingActionControl } from '../FloatingActions';
import { KeyboardAvoid } from '../KeyboardAvoid';
import { Panel } from '../Panel';
import { ScreenBackButton } from '../ScreenBackButton';
import { ScreenDeleteButton } from '../ScreenDeleteButton';
import { ScreenHeader } from '../ScreenHeader';
import { ScreenSaveButton } from '../ScreenSaveButton';
import { ScreenTitle } from '../ScreenTitle';
import { SelectorPills } from '../SelectorPills/SelectorPills';
import Shadow from '../Shadow';
import Spacer from '../Spacer';
import TextInputWithButtons from '../TextInputWithButtons';
import { styles } from './TaskItemForm.presets';
import { TaskItemFormProps } from './TaskItemForm.props';

const no_users: UserModel[] = [];

export const TaskItemForm = (props: TaskItemFormProps) => {
  const {
    onCancel,
    onSave,
    onChange,
    onDelete,
    users = no_users,
    item,
    mode,
    show_main_shopping_list_checkbox,
  } = props;

  const { width } = useWindowDimensions();
  const isSmall = width < 400;

  const { scrollHandler, floating_action_styles } = useFloatingActionControl();

  const input_ref = useRef<TextInput | null>();
  const scroller = useRef<Animated.ScrollView | null>();

  const onTextFieldChange = (attribute: keyof ChecklistModel) => (value: any) =>
    onChange({ ...item, [attribute]: value });

  const checkList1Label = getTaskFormContent(item);

  const onSubmitNewChecklistItem = (title: string) => {
    onChange({
      ...item,
      checklist: append(item.checklist, createChecklistItem({ title })),
    });

    input_ref.current?.focus();
    scroller.current?.scrollToEnd({ animated: true });
  };

  const onChangeItem = ({ text, id }: ItemChangeData) =>
    onChange({
      ...item,
      checklist: item.checklist.map((i) =>
        i.id === id ? { ...i, title: text } : i
      ),
    });

  const onRemoveItem = (id: string) =>
    onChange({
      ...item,
      checklist: item.checklist.filter(filterExcludeByID(id)),
    });

  return (
    <Screen>
      <Header>
        <ScreenBackButton onPress={onCancel} />
        <ScreenTitle>
          {mode === 'edit' ? 'Change list' : 'New list'}
        </ScreenTitle>
      </Header>

      <KeyboardAvoid>
        <Animated.ScrollView
          ref={(ref) => {
            scroller.current = ref;
          }}
          contentContainerStyle={styles.scroller}
          onScroll={scrollHandler}
          scrollEventThrottle={16}
          keyboardShouldPersistTaps="handled"
        >
          <TopFormPanel>
            <TopInputRow isSmall={isSmall}>
              <EmojiInputField
                emoji={item.emoji || '🗒️'}
                onChange={onTextFieldChange('emoji')}
                backgroundColor="#FFF5DA"
              />

              <LargeInput
                placeholder={`Add ${item.type} name`}
                value={item.title || ''}
                onChangeText={onTextFieldChange('title')}
                style={styles.largeInput}
              />
            </TopInputRow>

            <Spacer height="1px" color="lightgrey" />

            <SelectorRow>
              <SelectListTypeText variant="h3" weight="semibold">
                Select list type
              </SelectListTypeText>
              <TypeSelect
                disabled={!!item.primaryShoppingList}
                items={taskOption}
                value={item.type}
                onChange={onTextFieldChange('type')}
              />
            </SelectorRow>

            {show_main_shopping_list_checkbox ? (
              <Checkbox
                label="Main Shopping List"
                value={!!item.primaryShoppingList}
                onChange={onTextFieldChange('primaryShoppingList')}
                style={styles.checkbox}
              />
            ) : null}
          </TopFormPanel>

          <FormPanel>
            <FieldRow>
              <FieldIcon size={24} icon="user" color={icon_color} />

              <View style={layout.flex_1}>
                <DropdownFamily
                  onChange={onTextFieldChange('participants')}
                  title="Who is this for?"
                  style={styles.textInput1}
                  familyMembers={users}
                  value={item.participants}
                  multiSelect
                  showList
                />
              </View>
            </FieldRow>

            <Spacer height="1px" width="100%" color="lightgrey" />

            <FieldRow>
              <FieldIcon size={24} icon="tasks" color={icon_color} />

              <ChecklistContainer>
                {item.checklist.map((checklist_item) => (
                  <ChecklistFieldItem
                    key={checklist_item.id}
                    id={checklist_item.id}
                    text={checklist_item.title}
                    onChange={onChangeItem}
                    onRemove={onRemoveItem}
                  />
                ))}

                <TextInputWithButtons
                  blurOnSubmit={false}
                  ref={(ref) => (input_ref.current = ref)}
                  placeholder={checkList1Label}
                  onSubmit={onSubmitNewChecklistItem}
                  style={styles.textInputWithButtonsContainer}
                  textStyle={styles.textInputWithButtonsText}
                />
              </ChecklistContainer>
            </FieldRow>
          </FormPanel>
        </Animated.ScrollView>
      </KeyboardAvoid>
      <FloatingActions style={floating_action_styles}>
        {onDelete ? <ScreenDeleteButton onPress={onDelete} /> : null}
        <ScreenSaveButton onPress={onSave} />
      </FloatingActions>
    </Screen>
  );
};

const getTaskFormContent = (item: ChecklistModel) => {
  switch (item.type) {
    case 'Shopping':
      return 'What do you need to buy?';
    case 'ToDo':
      return 'What do you need to do?';
    case 'Checklist':
      return 'What needs to happen?';
  }
};

const taskOption = [
  {
    id: '6',
    label: 'Shopping',
    color: colors.primary[70],
    backgroundColor: colors.lightblue,
  },
  {
    id: '7',
    label: 'ToDo',
    color: colors.text.green,
    backgroundColor: colors.text.green_background,
  },
  {
    id: '8',
    label: 'Checklist',
    color: colors.pink.primary,
    backgroundColor: colors.pink.background,
  },
];

interface ItemChangeData {
  text: string;
  id: string;
}

interface Props {
  id: string;
  text: string;

  onChange(data: ItemChangeData): void;
  onRemove(id: string): void;
}

export const ChecklistItem = (props: Props) => {
  const { id, text, onChange, onRemove } = props;

  const onPressRemove = () => onRemove(id);
  const handleOnChange = (text: string) => onChange({ text, id });

  return (
    <Item>
      <ItemInput value={text} onChangeText={handleOnChange} />
      <ItemButtonContainer>
        <NewButton icon="times" variant="secondary" onPress={onPressRemove} />
      </ItemButtonContainer>
    </Item>
  );
};

const Screen = styled.View`
  flex: 1;
`;

const Header = styled(ScreenHeader)`
  justify-content: flex-start;
  gap: 20px;
`;

const TopFormPanel = styled(Panel)`
  padding: 25px 18px;
  gap: 25px;
`;

const FormPanel = styled(Panel)`
  padding: 18px;
  gap: 20px;
`;

const TopInputRow = styled.View<{ isSmall?: boolean }>`
  ${({ isSmall = false }) =>
    isSmall
      ? `flex-direction: column; justify-content: center;`
      : `flex-direction: row;`}
  gap: 25px;
  align-items: center;
`;

const FieldIcon = styled(FontAwesomeIcon)`
  margin-top: 15px;
`;

const TypeSelect = styled(SelectorPills)`
  padding-vertical: 0;
  max-width: 70%;
`;

const Item = styled(Shadow)`
  background-color: ${palette.white};
  padding-left: ${spacing[4]}px;
  padding-right: ${spacing[2]}px;

  flex-direction: row;
  align-items: center;
  margin-top: ${spacing[4]}px;
  border-radius: ${dimensions.buttonRadius}px;
  height: 56px;
`;

const ItemInput = styled.TextInput`
  flex: 1;
  ${font_variant_styles.p.regular}
`;

const ItemButtonContainer = styled.View`
  flex-grow: 0;
`;

const ChecklistContainer = styled.View`
  flex: 1;
  gap: 10px;
`;

const SelectorRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 30px;
  max-width: 100%;
`;

const SelectListTypeText = styled(Text)`
  opacity: 0.5;
  max-width: 30%;
`;

const FieldRow = styled.View`
  flex-direction: row;
  gap: 16px;
`;

const icon_color = 'rgba(5, 22, 51, 0.5)';
